import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  MapIcon,
  CreditCardIcon,
  ChartBarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const DashboardPage: React.FC = () => {
  const { profile, loading: authLoading } = useAuth();
  const { subscription, usage, loading: subLoading } = useSubscription();

  if (authLoading || subLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'diamond':
        return 'from-blue-600 to-purple-600';
      case 'platinum':
        return 'from-gray-400 to-gray-600';
      case 'gold':
        return 'from-yellow-500 to-yellow-600';
      default:
        return 'from-gray-500 to-gray-700';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Welcome back, {profile?.full_name || 'User'}!</h1>
        <p className="mt-2 text-gray-600">Manage your security intelligence subscription and access reports</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-md p-3">
              <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Subscription</p>
              <p className="text-2xl font-semibold text-gray-900 capitalize">{subscription?.tier || 'Free'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
              <DocumentTextIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Reports Accessed</p>
              <p className="text-2xl font-semibold text-gray-900">
                {usage?.lga_report?.count || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-100 rounded-md p-3">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">API Calls</p>
              <p className="text-2xl font-semibold text-gray-900">
                {usage?.api_call?.count || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3">
              <MapIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Regions</p>
              <p className="text-2xl font-semibold text-gray-900">2</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Subscription Details */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Subscription Details</h2>
            </div>
            <div className="px-6 py-5">
              {subscription ? (
                <div className="space-y-4">
                  <div className={`bg-gradient-to-r ${getTierColor(subscription.tier)} rounded-lg p-6 text-white`}>
                    <h3 className="text-2xl font-bold capitalize mb-2">{subscription.tier} Plan</h3>
                    <p className="text-3xl font-bold">${subscription.tier === 'gold' ? '30' : subscription.tier === 'platinum' ? '100' : '200'}/month</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Status</p>
                      <p className={`text-lg font-semibold ${
                        subscription.status === 'active' ? 'text-green-600' : 
                        subscription.status === 'canceled' ? 'text-red-600' : 'text-gray-600'
                      } capitalize`}>
                        {subscription.status}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Next Billing Date</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.current_period_end ? formatDate(subscription.current_period_end) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">LGA Reports Remaining</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.tier === 'diamond' ? 'Unlimited' : 
                         `${(subscription.tier === 'platinum' ? 10 : 5) - (usage?.lga_report?.count || 0)}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">API Calls Remaining</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.tier === 'diamond' ? 'Unlimited' : 
                         `${(subscription.tier === 'platinum' ? 1000 : 100) - (usage?.api_call?.count || 0)}`}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-4">
                    <Link
                      to="/subscription"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
                    >
                      <CreditCardIcon className="h-5 w-5 mr-2" />
                      Manage Subscription
                    </Link>
                    <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                      View Billing History
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No active subscription</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started with a subscription to access premium features</p>
                  <div className="mt-6">
                    <Link
                      to="/subscription"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                    >
                      View Plans
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="px-6 py-5">
              <nav className="space-y-2">
                <Link
                  to="/reports/overview"
                  className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <MapIcon className="h-5 w-5 mr-3 text-gray-400" />
                  Security Reports
                </Link>
                <Link
                  to="/reports/complete-report"
                  className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-3 text-gray-400" />
                  Complete Threat Assessment
                </Link>
                <Link
                  to="/profile"
                  className="flex items-center p-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ShieldCheckIcon className="h-5 w-5 mr-3 text-gray-400" />
                  Account Settings
                </Link>
              </nav>
            </div>
          </div>

          {/* Usage Alerts */}
          {subscription && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Usage Alert</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      You've used {usage?.lga_report?.count || 0} of your monthly LGA reports.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
